import { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { ShoppingCart, CurrencyDollar } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LineChart, Line, CartesianGrid, Tooltip } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });

export default function CreatorAffiliateRevenuePage() {
  const [commerce, setCommerce] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [roiProduct, setRoiProduct] = useState('');
  const [roiViews, setRoiViews] = useState('10000');
  const [roiComm, setRoiComm] = useState('6');
  const [roiResult, setRoiResult] = useState(null);
  const [calcingROI, setCalcingROI] = useState(false);

  useEffect(() => {
    axios.get(`${API}/creator/commerce-hub`, auth()).then(r => setCommerce(r.data)).catch(() => {});
    axios.get(`${API}/creator/income-forecast`, auth()).then(r => setForecast(r.data)).catch(() => {});
  }, []);

  const handleCalcROI = async () => {
    setCalcingROI(true);
    try { const r = await axios.post(`${API}/creator/reel-roi?product=${encodeURIComponent(roiProduct)}&views=${roiViews}&commission=${roiComm}`, {}, auth()); setRoiResult(r.data); }
    catch { toast.error('Failed'); } finally { setCalcingROI(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="creator-affiliate-revenue">
        <div>
          <h1 className="font-display text-2xl font-black text-[var(--text-primary)] tracking-tight">Affiliate Revenue</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Multi-platform affiliate income — Instagram, YouTube, Meesho, Flipkart, Amazon</p>
        </div>
        {commerce && (
          <div className="nx-metric-card rounded-md">
            <p className="text-3xl font-black text-[var(--success)] font-mono mb-3">INR {commerce.total_revenue?.toLocaleString()}</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {Object.entries(commerce.platforms || {}).map(([p, v]) => (
                <div key={p} className="p-2 rounded border border-[var(--border)] text-center">
                  <p className="text-[9px] text-[var(--text-tertiary)] capitalize">{p.replace('_', ' ')}</p>
                  <p className="text-sm font-bold text-[var(--text-primary)] font-mono">INR {v?.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Reel ROI */}
        <div className="nx-metric-card rounded-md">
          <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Reel ROI Calculator</h3>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <Input value={roiProduct} onChange={e => setRoiProduct(e.target.value)} placeholder="Product name" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] text-[10px]" />
            <Input value={roiViews} onChange={e => setRoiViews(e.target.value)} type="number" placeholder="Views" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] text-[10px]" />
            <Input value={roiComm} onChange={e => setRoiComm(e.target.value)} type="number" placeholder="Commission %" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] text-[10px]" />
          </div>
          <Button size="sm" onClick={handleCalcROI} disabled={calcingROI} className="bg-[var(--accent)] text-white text-[9px] h-7 mb-3">{calcingROI ? 'Calculating...' : 'Calculate ROI'}</Button>
          {roiResult && (
            <div className="grid grid-cols-3 gap-3">
              <div className="p-2.5 rounded border border-[var(--success-border)] bg-[var(--success-bg)] text-center"><p className="text-[8px] text-[var(--text-tertiary)]">Projected</p><p className="text-lg font-black text-[var(--success)] font-mono">INR {roiResult.projected_income?.toLocaleString()}</p></div>
              <div className="p-2.5 rounded border border-[var(--accent-border)] bg-[var(--accent-light)] text-center"><p className="text-[8px] text-[var(--text-tertiary)]">Citation Prob</p><p className="text-lg font-black text-[var(--accent)] font-mono">{roiResult.citation_probability}%</p></div>
              <div className="p-2.5 rounded border border-[var(--border)] text-center"><p className="text-[8px] text-[var(--text-tertiary)]">Range</p><p className="text-sm font-bold text-[var(--text-primary)] font-mono">INR {roiResult.low_estimate?.toLocaleString()} — {roiResult.high_estimate?.toLocaleString()}</p></div>
            </div>
          )}
        </div>
        {/* Forecast */}
        {forecast && (
          <div className="nx-metric-card rounded-md">
            <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">12-Week Income Forecast</h3>
            <div className="h-36 mb-3">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={forecast.weeks}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="week" tick={{ fill: 'var(--text-tertiary)', fontSize: 8 }} axisLine={false} tickFormatter={v => `W${v}`} />
                  <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 8 }} axisLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 10 }} />
                  <Line type="monotone" dataKey="affiliate" stroke="var(--accent)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="linkedin" stroke="var(--success)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded border border-[var(--border)] text-center"><p className="text-[8px] text-[var(--text-tertiary)]">4-Week</p><p className="text-sm font-bold text-[var(--text-primary)] font-mono">INR {forecast.total_4w?.toLocaleString()}</p></div>
              <div className="p-2 rounded border border-[var(--border)] text-center"><p className="text-[8px] text-[var(--text-tertiary)]">12-Week</p><p className="text-sm font-bold text-[var(--accent)] font-mono">INR {forecast.total_12w?.toLocaleString()}</p></div>
              <div className="p-2 rounded border border-[var(--success-border)] bg-[var(--success-bg)] text-center"><p className="text-[8px] text-[var(--text-tertiary)]">If SoM +5pts</p><p className="text-sm font-bold text-[var(--success)] font-mono">INR {forecast.som_boost_estimate?.toLocaleString()}</p></div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
